import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = useMemo(() => articles.find((a) => a.id === articleId), [articleId]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return article.relatedIds
      .map((rid) => articles.find((a) => a.id === rid))
      .filter(Boolean);
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line text-sm"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <nav className="bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li className="text-foreground-300">/</li>
              <li><Link to={`/category/${article.category === '电影' ? 'dianying' : article.category === '电视剧' ? 'dianshiju' : article.category === '综艺' ? 'zongyi' : 'dongman'}`} className="hover:text-primary-500 transition-colors">{article.category}</Link></li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </div>
        </nav>

        <article className="py-8 md:py-12">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500">#{tag}</span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line text-xs"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs"></i>
                  </div>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </div>
                  阅读 {article.readTime}
                </span>
              </div>
            </header>

            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-video object-cover"
              />
            </div>

            <div className="prose-custom text-foreground-700 leading-relaxed text-sm md:text-base">
              {article.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="mb-4">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70" aria-label="相关文章">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedArticles.map((rel) => {
                    if (!rel) return null;
                    return (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="group p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500">{rel.date} · {rel.readTime}</p>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              author: { '@type': 'Person', name: article.author },
              datePublished: article.date,
              image: article.imageUrl,
              articleSection: article.category,
              keywords: article.tags.join(', '),
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}